import { z } from 'zod';

/**
 * Schema for structured clinical summary content
 * Based on the medical format with headings for comprehensive patient summary
 */
export const StructuredClinicalSummarySchema = z.object({
  presentation: z.object({
    historyAndExamination: z.string().optional(),
    investigations: z.string().optional(),
    diagnoses: z.string().optional(),
    management: z.string().optional(),
    complications: z.string().optional(),
    hospitalEscalations: z.string().optional(),
    procedures: z.string().optional(),
  }),
  futureManagement: z.string().optional(),
  medications: z.object({
    medicationChanges: z.string().optional(),
    medicationsToTakeHome: z.string().optional(),
  }),
  allergiesAndAdverseReactions: z.string().optional(),
  informationForPatient: z.string().optional(),
});

export type StructuredClinicalSummary = z.infer<
  typeof StructuredClinicalSummarySchema
>;

/**
 * Google Vertex AI response schema for structured clinical summary
 * This matches the JSON schema format expected by Google's Vertex AI
 */
export const GoogleVertexClinicalSummarySchema = {
  type: 'object',
  properties: {
    presentation: {
      type: 'object',
      properties: {
        historyAndExamination: {
          type: 'string',
          description: 'History and examination findings from the patient care',
        },
        investigations: {
          type: 'string',
          description:
            'Diagnostic tests, lab results, and investigation findings',
        },
        diagnoses: {
          type: 'string',
          description:
            'Primary and secondary diagnoses established during care',
        },
        management: {
          type: 'string',
          description: 'Treatment plans and management strategies implemented',
        },
        complications: {
          type: 'string',
          description: 'Any complications that occurred during treatment',
        },
        hospitalEscalations: {
          type: 'string',
          description: 'Any escalations to hospital or emergency care',
        },
        procedures: {
          type: 'string',
          description: 'Medical procedures performed during the care episode',
        },
      },
    },
    futureManagement: {
      type: 'string',
      description: 'Future care plans and management recommendations',
    },
    medications: {
      type: 'object',
      properties: {
        medicationChanges: {
          type: 'string',
          description: 'Changes made to medications during the care episode',
        },
        medicationsToTakeHome: {
          type: 'string',
          description: 'Medications prescribed for home use or discharge',
        },
      },
    },
    allergiesAndAdverseReactions: {
      type: 'string',
      description: 'Known allergies and adverse reactions to note',
    },
    informationForPatient: {
      type: 'string',
      description: 'Information and instructions provided to the patient',
    },
  },
  required: ['presentation'],
};
